/**
 * Theme preference helpers — Phase 3.1.a.4.
 *
 * Reads / writes the admin theme (light vs dark) from localStorage and a
 * server-readable cookie, keeps the `<html data-theme>` attribute and the
 * theme-toggle button in sync, and exposes an idempotent boot helper.
 *
 * Pulled out of admin.js so the boot script can `import` it directly
 * instead of relying on top-of-file function declarations being in
 * lexical scope. Only the constants below are private to this module —
 * everything else is a small DOM/cookie utility.
 */

const THEME_STORAGE_KEY = 'admin_theme';
const THEME_COOKIE_KEY = THEME_STORAGE_KEY;
const THEME_DEFAULT = 'light';

export function readThemeCookie() {
  if (!document.cookie) return null;
  const entries = document.cookie.split(/;\s*/);
  for (const entry of entries) {
    const [rawName, ...valueParts] = entry.split('=');
    if (!rawName) continue;
    if (rawName === THEME_COOKIE_KEY) {
      try {
        return decodeURIComponent(valueParts.join('='));
      } catch {
        return null;
      }
    }
  }
  return null;
}

export function getThemePreference() {
  let value = null;
  try { value = localStorage.getItem(THEME_STORAGE_KEY); }
  catch { value = null; }
  if (value !== 'dark' && value !== 'light') {
    value = readThemeCookie();
  }
  return (value === 'dark' || value === 'light') ? value : null;
}

export function setThemePreference(theme) {
  const next = theme === 'dark' ? 'dark' : 'light';
  const maxAge = 365 * 24 * 60 * 60;
  try { localStorage.setItem(THEME_STORAGE_KEY, next); }
  catch {}
  const secure = (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') ? '; Secure' : '';
  const currentCookie = readThemeCookie();
  if (currentCookie !== next) {
    document.cookie = `${THEME_COOKIE_KEY}=${encodeURIComponent(next)}; path=/; max-age=${maxAge}; SameSite=Strict${secure}`;
  }
}

export function updateThemeToggle() {
  const toggle = document.getElementById('themeToggle');
  if (!toggle) return;
  const current = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
  const label = current === 'dark' ? '☀️ Light mode' : '🌙 Dark mode';
  toggle.textContent = label;
  toggle.setAttribute('aria-label', current === 'dark' ? 'Switch to light theme' : 'Switch to dark theme');
}

export function applyTheme(theme) {
  const next = theme === 'dark' ? 'dark' : 'light';
  document.documentElement.dataset.theme = next;
  setThemePreference(next);
  updateThemeToggle();
}

export function ensureThemeInitialized() {
  const stored = getThemePreference();
  const current = document.documentElement.dataset.theme === 'dark' ? 'dark'
    : document.documentElement.dataset.theme === 'light' ? 'light'
      : null;
  const initial = stored || current || THEME_DEFAULT;
  document.documentElement.dataset.theme = initial;
  setThemePreference(initial);
}
